import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { WeaponGroup } from '@api/disciplinesApi/interfaces';
import { useAuth } from '@context/AuthProvider';
import { filterWeaponGroupsMemberCanUse } from '@helpers/shootingEnrollment';
import { usePortalWeaponGroupCategoryMap } from '@reactQuery/shooting';
import { useTranslation } from 'react-i18next';
import { Dayjs } from 'dayjs';

import { ISubmitScoreDialog, ScorecardUpload, Step } from './interfaces';
import { Dialog } from '@components/dialogs/Dialog';
import { ChooseUploadMethodStep } from './steps/ChooseUploadMethodStep';
import { ConfirmationStep } from './steps/ConfirmationStep';
import { Button } from '@components/dialogs/Dialog/components/Button';
import { UploadImageDialog } from '@components/dialogs/UploadImageDialog';
import { enqueueSnackbar } from 'notistack';
import { useSubmitPortalScoreSubmissionMutation } from '@reactQuery/competitions';
import { SCORECARD_MIN_DIMENSION } from './helpers';
import { getShotOnConstraints } from './shotOnConstraints';
import { translateApiValidationError } from '@api/helpers';
import { AxiosError } from 'axios';
import { ValidationErrors } from '@api/interfaces';

export const SubmitScoreDialog: React.FC<ISubmitScoreDialog> = ({
  slug,
  startsOn,
  endsOn,
  weaponGroups,
  show = true,
  onClose,
  onSubmit,
}): JSX.Element => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { categoryMap } = usePortalWeaponGroupCategoryMap(show);
  const shotOnConstraints = useMemo(
    () => getShotOnConstraints(startsOn, endsOn),
    [startsOn, endsOn]
  );
  const [step, setStep] = useState<Step>('upload-method');
  const [showUploadDialog, setShowUploadDialog] = useState<boolean>(false);
  const [scorecard, setScorecard] = useState<ScorecardUpload | null>(null);
  const [value, setValue] = useState<string>('');
  const [shotOn, setShotOn] = useState<Dayjs | null>(
    shotOnConstraints.defaultDate
  );
  const [note, setNote] = useState<string>('');
  const [weaponGroup, setWeaponGroup] = useState<WeaponGroup | ''>('');

  const selectableWeaponGroups = useMemo(() => {
    if (weaponGroups.length <= 1 || categoryMap === undefined) {
      return weaponGroups;
    }

    return filterWeaponGroupsMemberCanUse(
      weaponGroups,
      currentUser?.member?.shootingClearances,
      categoryMap
    );
  }, [weaponGroups, categoryMap, currentUser?.member?.shootingClearances]);

  const mustSelectWeaponGroup = selectableWeaponGroups.length > 1;

  const { mutate: submitScore, isPending } =
    useSubmitPortalScoreSubmissionMutation();

  const reset = (): void => {
    setStep('upload-method');
    setShowUploadDialog(false);
    setScorecard(null);
    setValue('');
    setShotOn(shotOnConstraints.defaultDate);
    setNote('');
    setWeaponGroup('');
  };

  const handleClose = (): void => {
    reset();

    if (onClose) {
      onClose();
    }
  };

  const handlePreviousClick = (): void => {
    if (scorecard?.previewUrl) {
      URL.revokeObjectURL(scorecard.previewUrl);
    }

    setScorecard(null);
    setStep('upload-method');
  };

  const handleSubmit = (): void => {
    if (!scorecard || !shotOn || !value || !isShotOnValid) {
      return;
    }

    submitScore(
      {
        slug,
        image: scorecard.blob,
        filename: scorecard.filename,
        submission: {
          shotOn: shotOn.format('YYYY-MM-DD'),
          value: Number(value),
          note: note || null,
          ...(mustSelectWeaponGroup && weaponGroup
            ? { weaponGroup }
            : {}),
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar(
            t('competitions:portal.dialogs.submitScore.success'),
            {
              variant: 'success',
            }
          );
          handleClose();

          if (onSubmit) {
            onSubmit();
          }
        },
        onError: (error: AxiosError<ValidationErrors>) => {
          const errors = error.response?.data?.errors;
          const shotOnCode = errors?.shotOn?.[0];
          const weaponGroupCode = errors?.weaponGroup?.[0];
          const message =
            translateApiValidationError(shotOnCode) ??
            translateApiValidationError(weaponGroupCode) ??
            t('competitions:portal.dialogs.submitScore.failed');

          enqueueSnackbar(message, {
            variant: 'error',
          });
        },
      }
    );
  };

  const isShotOnValid = shotOnConstraints.isValid(shotOn);

  const canSubmit =
    Boolean(scorecard) &&
    isShotOnValid &&
    value.trim().length > 0 &&
    !Number.isNaN(Number(value)) &&
    (!mustSelectWeaponGroup || weaponGroup !== '');

  const Buttons = (
    <Fragment>
      {step === 'confirmation' && (
        <Button onClick={handlePreviousClick} label={t('buttons.previous')} />
      )}
      {step === 'confirmation' && (
        <Button
          onClick={handleSubmit}
          variant="primary"
          label={t('competitions:portal.dialogs.submitScore.submitButton')}
          disabled={!canSubmit || isPending}
        />
      )}
    </Fragment>
  );

  useEffect(() => {
    if (!show) {
      reset();
    }
  }, [show]);

  return (
    <Fragment>
      <Dialog
        show={show}
        title={t('competitions:portal.dialogs.submitScore.title')}
        onClose={handleClose}
        buttons={Buttons}
      >
        {step === 'upload-method' && (
          <ChooseUploadMethodStep
            onSelect={(method) => {
              if (method === 'image') {
                setShowUploadDialog(true);
              }
            }}
          />
        )}
        {step === 'confirmation' && scorecard && (
          <ConfirmationStep
            value={value}
            shotOn={shotOn}
            note={note}
            previewUrl={scorecard.previewUrl}
            minShotOnDate={shotOnConstraints.minDate}
            maxShotOnDate={shotOnConstraints.maxDate}
            onValueChange={setValue}
            onShotOnChange={setShotOn}
            onNoteChange={setNote}
            selectableWeaponGroups={selectableWeaponGroups}
            weaponGroup={weaponGroup}
            onWeaponGroupChange={setWeaponGroup}
          />
        )}
      </Dialog>

      <UploadImageDialog
        show={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        title={t('competitions:portal.dialogs.submitScore.uploadImage.title')}
        subTitle={t(
          'competitions:portal.dialogs.submitScore.uploadImage.subTitle'
        )}
        minDimension={SCORECARD_MIN_DIMENSION}
        onCrop={({ filename, blob }) => {
          const previewUrl = URL.createObjectURL(blob);

          setScorecard({
            filename,
            blob,
            previewUrl,
          });
          setShowUploadDialog(false);
          setStep('confirmation');
        }}
      />
    </Fragment>
  );
};
